<template>
  <div class="space-y-8 animate-in fade-in duration-500">
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center py-20 space-y-4">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p class="text-muted-foreground animate-pulse">Loading achievements...</p>
    </div>

    <template v-else>
    <!-- Badges Section -->
    <div
      class="border border-border rounded-2xl bg-white dark:bg-card p-6 shadow-sm overflow-hidden"
    >
      <!-- Section Header -->
      <div class="flex items-center gap-3 mb-8">
        <div class="p-2 bg-muted/30 rounded-lg">
          <Medal class="w-5 h-5 text-muted-foreground" />
        </div>
        <div class="flex items-center gap-2">
          <h3 class="font-bold text-foreground">Badges</h3>
          <span class="text-muted-foreground">|</span>
          <span class="text-sm text-muted-foreground"
            >Total Collected: {{ summary.total_badges_earned }}</span
          >
        </div>
      </div>

      <!-- Badges Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          v-for="(badge, index) in badges"
          :key="index"
          class="flex items-start gap-4 p-4 border border-border/50 bg-muted/5 rounded-xl hover:shadow-md transition-all group"
        >
          <div class="relative shrink-0">
            <img
              :src="getAchievementIcon(badge.icon)"
              :alt="badge.title"
              class="w-16 h-16 object-contain group-hover:scale-110 transition-transform"
            />
          </div>
          <div class="space-y-1">
            <h4 class="font-bold text-foreground text-[15px]">
              {{ badge.title }}
            </h4>
            <p class="text-[12px] text-muted-foreground leading-snug">
              {{ badge.description }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Milestones Section -->
    <div
      class="border border-border rounded-2xl bg-white dark:bg-card p-6 shadow-sm"
    >
      <!-- Section Header -->
      <div class="flex items-center gap-3 mb-8">
        <div class="p-2 bg-muted/30 rounded-lg">
          <LayoutGrid class="w-5 h-5 text-muted-foreground" />
        </div>
        <div class="flex items-center gap-2">
          <h3 class="font-bold text-foreground">Milestones</h3>
          <span class="text-muted-foreground">|</span>
          <span class="text-sm text-muted-foreground"
            >Completed: {{ summary.total_milestones_reached }}</span
          >
        </div>
      </div>

      <!-- Sub-tabs -->
      <div
        class="flex items-center gap-8 border-b border-border/50 mb-8 overflow-x-auto scrollbar-hide"
      >
        <button
          v-for="tab in milestoneTabs"
          :key="tab"
          @click="activeMilestoneTab = tab"
          class="pb-4 text-sm font-medium transition-all relative whitespace-nowrap"
          :class="[
            activeMilestoneTab === tab
              ? 'text-foreground border-b-2 border-foreground'
              : 'text-muted-foreground hover:text-foreground',
          ]"
        >
          {{ tab }}
        </button>
      </div>

      <!-- Milestones List -->
      <div class="space-y-4">
        <div
          v-for="(milestone, index) in filteredMilestones"
          :key="index"
          class="flex items-center gap-6 p-4 border border-border/50 rounded-xl bg-muted/5 group hover:bg-muted/10 transition-all"
          :class="{ 'opacity-60': milestone.locked }"
        >
          <!-- Icon Container -->
          <div
            class="w-12 h-12 flex items-center justify-center bg-white dark:bg-muted/20 rounded-lg shrink-0 shadow-sm border border-border/10"
          >
            <img
              v-if="milestone.iconType === 'image'"
              :src="getAchievementIcon(milestone.icon)"
              class="w-8 h-8 object-contain"
            />
            <component
              v-else
              :is="milestone.icon"
              class="w-6 h-6 text-muted-foreground"
            />
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 space-y-2">
            <div class="flex items-center gap-2 overflow-hidden">
              <h4 class="font-bold text-foreground truncate">
                {{ milestone.title }}
              </h4>
              <span class="text-muted-foreground text-xs shrink-0">•</span>
              <p class="text-xs text-muted-foreground truncate">
                {{ milestone.description }}
              </p>
            </div>

            <!-- Progress Bar -->
            <div
              class="relative w-full h-1.5 bg-muted/30 rounded-full overflow-hidden"
            >
              <div
                class="absolute top-0 left-0 h-full transition-all duration-1000 ease-out"
                :class="
                  milestone.progress >= 100
                    ? 'bg-foreground'
                    : 'bg-muted-foreground/30'
                "
                :style="{ width: `${milestone.progress}%` }"
              ></div>
            </div>
            <div class="flex justify-between items-center">
              <span
                v-if="milestone.progress >= 100"
                class="text-[10px] font-bold text-muted-foreground"
                >Completed</span
              >
              <span v-else class="text-[10px] text-muted-foreground"
                >{{ milestone.current }}/{{ milestone.target }}</span
              >
            </div>
          </div>

          <!-- Status / Action -->
          <div class="flex items-center gap-3 shrink-0">
            <div
              v-if="milestone.progress >= 100"
              class="p-1.5 bg-[#1BFF00]/10 rounded-full border border-[#1BFF00]/20"
            >
              <img src="@/assets/users/tick.svg" class="w-3.5 h-3.5" />
            </div>
            <div
              v-else
              class="p-1.5 bg-muted/20 rounded-full border border-border/50"
            >
              <Lock
                v-if="milestone.locked"
                class="w-3.5 h-3.5 text-muted-foreground"
              />
              <Medal v-else class="w-3.5 h-3.5 text-muted-foreground/30" />
            </div>
            <button
              class="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
            >
              <Share2 class="w-4 h-4" />
            </button>
          </div>
        </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  Medal,
  LayoutGrid,
  Lock,
  Share2,
  Sun,
  Timer,
  Handshake,
  BookOpen,
  Globe,
  Trophy,
} from "lucide-vue-next";
import apiClient from "@/axios";

export default {
  name: "AchievementsTab",
  components: {
    Medal,
    LayoutGrid,
    Lock,
    Share2,
    Sun,
    Timer,
    Handshake,
    BookOpen,
    Globe,
    Trophy,
  },
  data() {
    return {
      loading: true,
      activeMilestoneTab: "general",
      summary: { total_badges_earned: 0, total_milestones_reached: 0 },
      milestoneTabs: [],
      badges: [],
      /* 
      PREVIOUS HARDCODED BADGES - DO NOT DELETE
      badges: [
        {
          title: "Unstoppable",
          description:
            "You are an unstoppable force! the mark of true excellence.",
          icon: "unstoppable.svg",
        },
        {
          title: "Achiever",
          description:
            "You've shown that consistent progress leads to mastery.",
          icon: "achiever.svg",
        },
        {
          title: "Unstoppable",
          description:
            "You are an unstoppable force! the mark of true excellence.",
          icon: "unstoppable.svg",
        },
        {
          title: "Unstoppable",
          description:
            "You are an unstoppable force! the mark of true excellence.",
          icon: "unstoppable.svg",
        },
        {
          title: "Pioneer",
          description: "Ahead of the curve! You are among the first 100 users!",
          icon: "pioneer.svg",
        },
        {
          title: "Pioneer",
          description: "Ahead of the curve! You are among the first 100 users!",
          icon: "pioneer.svg",
        },
        {
          title: "Pioneer",
          description: "Ahead of the curve! You are among the first 100 users!",
          icon: "pioneer.svg",
        },
        {
          title: "Pioneer",
          description: "Ahead of the curve! You are among the first 100 users!",
          icon: "pioneer.svg",
        },
      ],
      */
      milestones: [],
      /* 
      PREVIOUS HARDCODED MILESTONES - DO NOT DELETE
      milestones: [
        {
          tab: "General",
          title: "7-Day Streak",
          description: "Log in everyday for a week",
          icon: "Sun",
          iconType: "lucide",
          progress: 28, // 2/7
          current: 2,
          target: 7,
          locked: false,
        },
        {
          tab: "General",
          title: "30-Day Streak",
          description: "Stay active daily for a month",
          icon: "Timer",
          iconType: "lucide",
          progress: 100,
          current: 30,
          target: 30,
          locked: false,
        },
        {
          tab: "General",
          title: "Networking",
          description: "Build 15 professional connections",
          icon: "handshake.png",
          iconType: "image",
          progress: 100,
          current: 15,
          target: 15,
          locked: false,
        },
        {
          tab: "General",
          title: "Knowledge Seeker",
          description: "Access 5 learning resources",
          icon: "book.png",
          iconType: "image",
          progress: 20, // 2/10 in pic? wait target is 5 but says 2/10 in pic description?
          current: 2,
          target: 10,
          locked: false,
        },
        {
          tab: "General",
          title: "Talent mentorship",
          description: "Mentor one or more talents",
          icon: "Globe",
          iconType: "lucide",
          progress: 0,
          current: 0,
          target: 1,
          locked: true,
        },
        {
          tab: "General",
          title: "Pioneer user",
          description: "Among the first 500 users",
          icon: "trophy.svg",
          iconType: "image",
          progress: 100,
          current: 1,
          target: 1,
          locked: false,
        },
      ],
      */
    };
  },
  async mounted() {
    await this.fetchData();
  },
  computed: {
    completedMilestonesCount() {
      return this.milestones.filter((m) => m.progress >= 100).length;
    },
    filteredMilestones() {
      const activeTabLower = this.activeMilestoneTab.toLowerCase();
      return this.milestones.filter((m) => m.tab.toLowerCase() === activeTabLower);
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchCategories(),
          this.fetchAchievements(),
          this.fetchSummary(),
        ]);
      } catch (e) {
        console.error("Error fetching achievements data:", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchSummary() {
      try {
        const { data } = await apiClient.get("/web/achievements/badges-and-milestones");
        this.summary = data;
      } catch (e) {
        console.error("Error fetching summary:", e);
      }
    },
    async fetchCategories() {
      try {
        const { data } = await apiClient.get("/web/achievements/categories");
        // We keep all categories for the tabs, including 'milestones'
        this.milestoneTabs = data.map((cat) => cat.charAt(0).toUpperCase() + cat.slice(1));

        if (
          this.milestoneTabs.length > 0 &&
          !this.milestoneTabs.includes(this.activeMilestoneTab)
        ) {
          // Default to 'General' if exists, otherwise first tab
          this.activeMilestoneTab = this.milestoneTabs.find(t => t === 'General') || this.milestoneTabs[0];
        }
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    },
    async fetchAchievements() {
      try {
        const { data } = await apiClient.get("/web/achievements/my-achievements");

        // The top section shows 'Awards'
        this.badges = data
          .filter((a) => a.is_completed)
          .map((a) => ({
            title: a.name,
            description: a.description,
            icon: a.badge
              ? a.badge.includes(".")
                ? a.badge
                : `${a.badge}.svg`
              : `${a.achievement_type}.svg`,
          }));

        // The bottom section shows categorized progress for everything
        this.milestones = data.map((a) => {
          const hasBadge = a.badge && a.badge.length > 0;
          const iconName = hasBadge ? a.badge : a.icon || a.achievement_type;
          const isLucide = !iconName.includes(".") && !hasBadge;

          return {
            tab: a.category.charAt(0).toUpperCase() + a.category.slice(1),
            title: a.name,
            description: a.description,
            icon: isLucide ? iconName : iconName.includes(".") ? iconName : `${iconName}.svg`,
            iconType: isLucide ? "lucide" : "image",
            progress: (a.progress / a.requirement_count) * 100,
            current: a.progress,
            target: a.requirement_count,
            locked: !a.is_completed,
          };
        });
      } catch (e) {
        console.error("Error fetching achievements:", e);
      }
    },
    getAchievementIcon(iconName) {
      if (!iconName) return require(`@/assets/users/TrophyStar.svg`);
      try {
        if (iconName.endsWith(".svg") || iconName.endsWith(".png")) {
          return require(`@/assets/achievements/${iconName}`);
        }
        return require(`@/assets/users/TrophyStar.svg`);
      } catch (e) {
        return require(`@/assets/users/TrophyStar.svg`);
      }
    },
  },
};
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
